import { useRuns } from "@/api/runs";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { RunListAgGrid } from "@/components/runs/RunListAgGrid";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Tenant-wide runs feed. AG-Grid Quartz on the FlowStack theme over
 * the B1 /api/runs reader. Filters serialize through URL params so
 * paste/bookmark round-trips a filtered view.
 */
export function RunListPage(): JSX.Element {
  const { data: runs, isLoading, isError, error } = useRuns();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Runs" description="Tenant-wide run feed across all projects." />
        <Skeleton className="h-10 w-full max-w-md" />
        <Skeleton className="h-[560px] w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Runs" description="Tenant-wide run feed across all projects." />
        <EmptyState
          title="Could not load runs."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Runs" description="Tenant-wide run feed across all projects." />
        <EmptyState
          title="No runs yet."
          description="Submit a Tile or a Flow from the SDK or the submission UI to populate this list."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Runs"
        description={
          runs.length === 1
            ? "1 run across all projects you can access."
            : `${runs.length} runs across all projects you can access.`
        }
      />
      <RunListAgGrid runs={runs} showProjectColumn />
    </div>
  );
}
